import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  getPersonDetails,
  getPersonsByListing,
  selectReceiver,
  saveSignature,
  SecurityDepositPerson,
} from "./DataServices/SecurityDepositDataServices";
import SignaturePad from "./SignaturePad";

const SecurityDepositReceiverSelection: React.FC = () => {
  const { listingId, personId } = useParams<{
    listingId: string;
    personId: string;
  }>();
  const [persons, setPersons] = useState<SecurityDepositPerson[]>([]);
  const [currentPerson, setCurrentPerson] =
    useState<SecurityDepositPerson | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedReceiverId, setSelectedReceiverId] = useState<string | null>(
    null,
  );
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectionCounts, setSelectionCounts] = useState<{
    [key: string]: number;
  }>({});
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [capturedSignature, setCapturedSignature] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!listingId || !personId) {
      setError("Invalid link. Please use the link from your email.");
      setLoading(false);
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId, personId]);

  const fetchData = async () => {
    try {
      // Validate the person
      const personData = await getPersonDetails(listingId!, personId!);
      setCurrentPerson(personData);

      // Get all persons for the listing
      const personsData = await getPersonsByListing(listingId!);
      setPersons(personsData);

      // Calculate selection counts - count how many people have LastSelectedReceiverId pointing to each person
      const counts: { [key: string]: number } = {};
      personsData.forEach((person: SecurityDepositPerson) => {
        if (person.lastSelectedReceiverId) {
          counts[person.lastSelectedReceiverId] =
            (counts[person.lastSelectedReceiverId] || 0) + 1;
        }
      });
      setSelectionCounts(counts);

      // Set who the CURRENT PERSON selected using their LastSelectedReceiverId
      if (personData.lastSelectedReceiverId) {
        setSelectedReceiverId(personData.lastSelectedReceiverId);
      }

      setLoading(false);
    } catch (error: any) {
      console.error("Error fetching data:", error);
      setError(error.message || "Failed to load data. Please try again later.");
      setLoading(false);
    }
  };

  const handleSelectReceiver = (selectedPersonId: string) => {
    // Just update local state, don't call API yet
    setSelectedReceiverId(selectedPersonId);
    // Show signature pad
    setShowSignaturePad(true);
  };

  const handleSignatureCapture = (signature: string) => {
    // Store the signature and show confirmation popup
    setCapturedSignature(signature);
    setShowSignaturePad(false);
    setShowConfirmSubmit(true);
  };

  const handleClearSignature = () => {
    // Clear is handled by SignaturePad component
  };

  const handleFinalSubmit = async () => {
    if (!capturedSignature || !selectedReceiverId) return;

    try {
      // First, select the receiver
      await selectReceiver(listingId!, personId!, selectedReceiverId);

      // Then save signature with the selected receiver
      await saveSignature(
        listingId!,
        personId!,
        capturedSignature,
        selectedReceiverId,
      );

      // Refetch data from backend to get updated counts and states
      const personsData = await getPersonsByListing(listingId!);
      setPersons(personsData);

      // Recalculate selection counts
      const counts: { [key: string]: number } = {};
      personsData.forEach((person: SecurityDepositPerson) => {
        if (person.lastSelectedReceiverId) {
          counts[person.lastSelectedReceiverId] =
            (counts[person.lastSelectedReceiverId] || 0) + 1;
        }
      });
      setSelectionCounts(counts);

      // Show success message
      setSubmitSuccess(true);
      setShowConfirmSubmit(false);
      setCapturedSignature(null);
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error: any) {
      console.error("Error saving signature:", error);
      setError(error.message || "Failed to save signature. Please try again.");
      setTimeout(() => setError(null), 5000);
      // Return to signature pad on error
      setShowConfirmSubmit(false);
      setShowSignaturePad(true);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-lg text-gray-700">Loading...</div>
      </div>
    );
  }

  if (error && !currentPerson) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="max-w-md p-6 bg-red-50 border border-red-200 rounded-lg">
          <h2 className="text-xl font-bold text-red-800 mb-2">Access Error</h2>
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  const alreadySelected = selectedReceiverId !== null;
  const selectedPerson = persons.find((p) => p.isReceiver);

  // Show signature pad after selection
  if (showSignaturePad && selectedReceiverId) {
    const receiver = persons.find((p) => p.id === selectedReceiverId);
    return (
      <div className="min-h-screen bg-gray-50 py-8 sm:py-12 flex flex-col justify-center items-center">
        <div className="max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="p-6 sm:p-10">
              {/* Header */}
              <div className="mb-8">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4 tracking-tight">
                  Confirm with Signature
                </h1>
                {currentPerson && receiver && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-6 shadow-sm">
                    <p className="text-gray-800 mb-2 font-medium text-lg">
                      Hi{" "}
                      <span className="text-blue-700">
                        {currentPerson.name}
                      </span>
                      ,
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      You have selected{" "}
                      <strong className="text-gray-900">{receiver.name}</strong>{" "}
                      as the security deposit receiver.
                    </p>
                    <p className="text-gray-500 mt-2 text-sm">
                      Please provide your signature below to sustainably confirm
                      this selection.
                    </p>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && currentPerson && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-start gap-3 shadow-sm">
                  <span className="text-red-500">⚠</span>
                  {error}
                </div>
              )}

              {/* Signature Pad */}
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-5 sm:p-6 mb-8 shadow-inner">
                <SignaturePad
                  onSignatureCapture={handleSignatureCapture}
                  onClear={handleClearSignature}
                />
              </div>

              {/* Footer */}
              <div className="pt-4 flex justify-start">
                <button
                  onClick={() => {
                    setShowSignaturePad(false);
                    setSelectedReceiverId(null);
                  }}
                  className="text-gray-500 hover:text-gray-800 font-medium transition-colors border-b border-transparent hover:border-gray-800 pb-0.5"
                >
                  ← Back to Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show confirmation popup after signature
  if (showConfirmSubmit && selectedReceiverId && capturedSignature) {
    const receiver = persons.find((p) => p.id === selectedReceiverId);
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10 max-w-lg w-full border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 text-3xl shadow-sm border border-blue-100">
              ✍️
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">
              Ready to Submit?
            </h2>
            <p className="text-gray-500 text-sm">
              Please review your selection before finalizing.
            </p>
          </div>

          {currentPerson && receiver && (
            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 mb-8 mt-2 space-y-4">
              <div className="flex justify-between items-center border-b border-gray-200 pb-4">
                <p className="text-sm text-gray-500">Your Name</p>
                <p className="font-semibold text-gray-900">
                  {currentPerson.name}
                </p>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-4">
                <p className="text-sm text-gray-500">Selected Receiver</p>
                <p className="font-semibold text-gray-900">{receiver.name}</p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">Signature Status</p>
                <p className="text-green-600 font-semibold flex items-center gap-1">
                  <span className="bg-green-100 text-green-700 w-5 h-5 flex items-center justify-center rounded-full text-xs">
                    ✓
                  </span>{" "}
                  Signed
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 flex items-start gap-3 rounded-xl shadow-sm">
              <span className="text-red-500">⚠</span>
              {error}
            </div>
          )}

          <div className="flex flex-col gap-3">
            <button
              onClick={handleFinalSubmit}
              className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl shadow-md transition-all font-semibold focus:ring-4 focus:ring-blue-300"
            >
              Confirm and Submit
            </button>
            <button
              onClick={() => {
                setShowConfirmSubmit(false);
                setCapturedSignature(null);
                setShowSignaturePad(true);
              }}
              className="w-full py-3 bg-white text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium shadow-sm hover:shadow"
            >
              ← Back to Sign
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    const selectedPerson = persons.find((p) => p.id === selectedReceiverId);

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-xl p-10 max-w-lg w-full text-center border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-green-400 to-emerald-500"></div>
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-50 flex items-center justify-center text-green-500 text-4xl shadow-sm border border-green-100 relative">
            <span className="absolute inset-0 rounded-full border-4 border-green-200 animate-pulse"></span>
            ✓
          </div>

          <h2 className="text-2xl font-extrabold text-gray-900 mb-3">
            Receiver Selected Successfully!
          </h2>

          <p className="text-gray-500 mb-6 text-sm">
            Your selection for the receiver has been confirmed.
          </p>

          {selectedPerson && (
            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 mb-6 text-left shadow-inner">
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1">
                Selected Receiver
              </p>
              <p className="text-lg font-bold text-gray-900 mb-0.5">
                {selectedPerson.name}
              </p>
              <p className="text-sm text-gray-500">✉ {selectedPerson.email}</p>
            </div>
          )}

          <p className="text-sm text-gray-500 border-t border-gray-100 pt-6">
            Once all tenants confirm, the final receiver will be selected.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <header className="bg-white shadow-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
            The Downtown Apartments, LLC
          </h1>
        </div>
      </header>

      <main className="py-8 sm:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 sm:p-8">
              {/* Header */}
              <div className="mb-8 text-center">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-2 tracking-tight">
                  Security Deposit Refund
                </h2>
                {currentPerson && (
                  <p className="text-lg text-gray-600">
                    Hi <strong>{currentPerson.name}</strong>, welcome back.
                  </p>
                )}
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded-r-lg mb-8 shadow-sm">
                <p className="text-gray-800 leading-relaxed mb-3">
                  Your move-out is complete, and we're now ready to process your
                  security deposit refund.
                </p>
                <p className="text-gray-800 font-semibold leading-relaxed">
                  <strong>Important:</strong> All tenants and guarantors must
                  agree on one single person who will receive the full security
                  deposit refund. Please coordinate and choose the designated
                  receiver below.
                </p>
              </div>

              {/* Current User Info */}
              {currentPerson && (
                <div className="mb-8 p-4 bg-indigo-50 border border-indigo-200 rounded-lg text-center shadow-sm">
                  <p className="text-md text-indigo-800">
                    You are signing in as:{" "}
                    <strong className="font-bold">{currentPerson.name}</strong>{" "}
                    ({currentPerson.type})
                  </p>
                </div>
              )}

              {/* Success Message */}
              {submitSuccess && (
                <div
                  className="mb-6 p-4 bg-green-100 border border-green-300 text-green-800 rounded-lg flex items-center gap-4 shadow-md"
                  role="alert"
                >
                  <svg
                    className="w-6 h-6 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="font-bold">Selection Confirmed!</p>
                    <p>
                      Your choice has been saved. Once everyone agrees, we will
                      process the refund.
                    </p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && currentPerson && (
                <div
                  className="mb-6 p-4 bg-red-100 border border-red-300 text-red-800 rounded-lg shadow-md"
                  role="alert"
                >
                  {error}
                </div>
              )}

              {/* Already Selected Warning */}
              {alreadySelected && selectedPerson && (
                <div
                  className="mb-6 p-4 bg-yellow-100 border border-yellow-300 text-yellow-800 rounded-lg shadow-md"
                  role="alert"
                >
                  <p className="font-bold">Current Selection</p>
                  <p className="mt-1">
                    You have currently selected{" "}
                    <strong>{selectedPerson.name}</strong>. You can change this
                    by selecting someone else below.
                  </p>
                </div>
              )}

              {/* Selection Section */}
              <div className="space-y-5">
                <h3 className="text-2xl font-bold text-gray-800 mb-4 border-b-2 pb-3">
                  Select the Recipient
                </h3>

                {persons.map((person) => {
                  const isCurrentPersonSelection =
                    selectedReceiverId === person.id;
                  const voteCount = selectionCounts[person.id] || 0;

                  return (
                    <div
                      key={person.id}
                      className={`border-2 rounded-xl p-5 cursor-pointer transition-all duration-300 ease-in-out transform hover:-translate-y-1 ${
                        isCurrentPersonSelection
                          ? "border-blue-600 bg-blue-50 shadow-lg ring-2 ring-blue-300"
                          : "border-gray-200 hover:border-blue-400 hover:shadow-md"
                      }`}
                      onClick={() => handleSelectReceiver(person.id)}
                      role="radio"
                      aria-checked={isCurrentPersonSelection}
                      tabIndex={0}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h4 className="font-bold text-xl text-gray-900">
                              {person.name}
                            </h4>
                            {person.id === currentPerson?.id && (
                              <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full font-semibold">
                                YOU
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {person.email}
                          </p>
                          <div className="flex items-center gap-3 mt-3">
                            <span className="inline-block text-xs bg-gray-200 text-gray-800 px-3 py-1 rounded-full uppercase font-bold tracking-wider">
                              {person.type}
                            </span>
                            {voteCount > 0 && (
                              <span className="text-sm text-gray-600 font-medium">
                                {voteCount}{" "}
                                {voteCount === 1 ? "person has" : "people have"}{" "}
                                selected
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          {isCurrentPersonSelection ? (
                            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white shadow-inner">
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="3"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-8 h-8 border-2 border-gray-300 rounded-full group-hover:border-blue-500 transition-colors"></div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {persons.length === 0 && (
                <div className="text-center text-gray-500 py-10">
                  <p className="font-semibold">
                    No tenants or guarantors found for this listing.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600 mb-3">
                If you have any questions, please don't hesitate to reach out.
              </p>
              <div className="text-sm text-gray-800">
                <p className="font-bold">The Downtown Apartments, LLC</p>
                <p>212-343-3014 | rental@thedowntownapts.com</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SecurityDepositReceiverSelection;
